import { ChevronLeft, ChevronRight } from "lucide-react";

import { Input } from "@/shadcn/ui/input";

import { Button } from "../Button/Button";

export type CalendarToolbarProps<T extends string> = {
  title: string;
  compact?: boolean;
  subtitle?: string;
  monthNavigation?: { label: string; value: string; onChange: (month: string) => void };
  view: T;
  views: readonly { value: T; label: string }[];
  todayLabel: string;
  previousLabel: string;
  nextLabel: string;
  onViewChange: (view: T) => void;
  onToday: () => void;
  onPrevious: () => void;
  onNext: () => void;
};

export const CalendarToolbar = <T extends string>({
  title,
  compact = false,
  subtitle,
  monthNavigation,
  view,
  views,
  todayLabel,
  previousLabel,
  nextLabel,
  onViewChange,
  onToday,
  onPrevious,
  onNext,
}: CalendarToolbarProps<T>) => (
  <div
    className={
      compact
        ? "flex flex-wrap items-center justify-between gap-3"
        : "flex flex-wrap items-start justify-between gap-6"
    }
  >
    <div>
      <h2 className={compact ? "type-heading-md tracking-tight" : "type-heading-lg tracking-tight"}>
        {title}
      </h2>
      {subtitle && <p className="mt-1 type-body-sm text-muted-foreground">{subtitle}</p>}
    </div>
    <div
      className={
        compact ? "flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-4"
      }
    >
      {monthNavigation && (
        <Input
          type="month"
          min="0001-01"
          max="9999-12"
          aria-label={monthNavigation.label}
          value={monthNavigation.value}
          className="h-8 w-44 type-body-sm"
          onChange={(event) => {
            const month = event.currentTarget.value;
            if (/^(?!0000)\d{4}-(0[1-9]|1[0-2])$/.test(month)) monthNavigation.onChange(month);
          }}
        />
      )}
      <div className="flex items-center gap-1">
        <Button variant="secondary" size="sm" onClick={onToday}>
          {todayLabel}
        </Button>
        <Button variant="ghost" size="sm" aria-label={previousLabel} onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" aria-label={nextLabel} onClick={onNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex rounded-xl border border-border/60 bg-muted/30 p-1" role="group">
        {views.map((option) => (
          <Button
            key={option.value}
            aria-pressed={view === option.value}
            size="sm"
            variant="ghost"
            className={
              view === option.value
                ? "rounded-lg bg-background shadow-sm hover:bg-background"
                : "rounded-lg text-muted-foreground"
            }
            onClick={() => onViewChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  </div>
);
